import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import { DatePicker } from '@mui/lab';
import DateAdapter from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import {
  Autocomplete,
  Box,
  Checkbox,
  DialogActions,
  DialogContent,
  Grid,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import {
  FeeArrangementDTO,
  IOurEntity,
  createNewFeeArrangement,
  getAllFeeArrangementLevelsForClient,
  getClientContactAutocompleteOptions,
  getClientOurEntityDetail,
} from 'wisestep-api';
import {
  ChatBoxHeader,
  ControlledTextInput,
  Label,
  WsButton,
  WsDialog,
  WsSelect,
  WsSelectObj,
  WsToggleButton,
  WsTypoVariants,
  WsTypography,
} from 'wisestep-base';
import { object } from 'yup';

import { useFetch } from '../../../hooks/useFetch';
import useWsAutocomplete from '../../../hooks/useWsAutocomplete';
import { failed, success, warning } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { CurrencyDropdown } from '../../clientDetails/components/contractArrangementsTab/AddEditFeeArrangement';
import IFeeArrangement from '../../clientDetails/types/IFeeArrangement';
import { ArrangementLevel } from '../../clientsSettings/components/FeeArrangementLevelSettings';
import { getAutocompleteLabelForClientContact } from '../../jobs/helper';

export interface IComponentProps {
  clientId: string;
  openDialog: boolean;
  handleClose: () => void;
  onSave: (data: FeeArrangementDTO) => void;
}

const validationSchema = object().shape({});

const AddFeeArrangementDialog = ({
  clientId,
  openDialog,
  handleClose,
  onSave,
}: IComponentProps) => {
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [clientContactOptions, setClientContactOptions] = useState([]);
  const clientContactAutocompleteProps = useWsAutocomplete('autocompleteLabel');
  const [feeArrLevelOptions, setFeeArrLevelOptions] = useState<WsSelectObj[]>([]);
  const [ourEntities] = useFetch<IOurEntity[]>(() => getClientOurEntityDetail(clientId));
  const methods = useForm<IFeeArrangement>({
    defaultValues: {
      feeType: 'FIXED',
    },
    resolver: yupResolver(validationSchema),
  });
  const feeType = methods.watch('feeType');

  const contractTypes = [
    {
      label: 'Permanent',
      value: 'PERMANENT',
    },
  ];

  const empGuaranteePeriodUnits = [
    {
      label: 'Days',
      value: 'DAYS',
    },
    {
      label: 'Weeks',
      value: 'WEEKS',
    },
    {
      label: 'Months',
      value: 'MONTHS',
    },
  ];

  const feeTypes = [
    {
      label: 'Percentage',
      value: 'PERCENTAGE',
    },
    {
      label: 'Fixed',
      value: 'FIXED',
    },
  ];

  const onSubmit = async (formData) => {
    let data: any;
    const arrangement = {
      ...formData,
      clientId,
    };
    try {
      ({ data } = await createNewFeeArrangement(arrangement));
      setNotification(success(`Contract has been added successfully !!`));
      onSave(data);
      handleClose();
    } catch (error: any) {
      if (error.response.status === 400)
        setNotification(warning(`Oops, contract already exists!!`));
      else setNotification(failed('Something went wrong !!'));
    }
  };

  useEffect(() => {
    const getArrangementLevelOptions = async () => {
      const levels: ArrangementLevel[] = (await getAllFeeArrangementLevelsForClient(clientId)).data;
      const levelOptions: WsSelectObj[] = [];
      levels.forEach((element) => {
        levelOptions.push({ label: element.name, value: element.value ?? '' });
      });
      setFeeArrLevelOptions(levelOptions);
    };
    getArrangementLevelOptions();
  }, [clientId]);

  return (
    <WsDialog size="medium" openDialog={openDialog}>
      <ChatBoxHeader>
        <WsTypography variant={WsTypoVariants.SUB_TITLE} display="inline" bold={true}>
          Add Fee Arrangement
        </WsTypography>
        <CloseIcon
          sx={{ cursor: 'pointer', float: 'right', mt: 0.5 }}
          fontSize={'small'}
          onClick={handleClose}
        />
      </ChatBoxHeader>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <DialogContent>
            <Box borderRadius={1.5}>
              <Grid container py={1} justifyContent="space-between">
                <Grid item xs={12} sm={6} pr={1}>
                  <Label>Organization Entity *</Label>
                  <Controller
                    name="ourEntityIds"
                    control={methods.control}
                    defaultValue={[]}
                    render={({ field: { onChange, value = [] } }) => (
                      <Select
                        onChange={onChange}
                        value={value}
                        multiple
                        fullWidth
                        defaultValue={[]}
                        size="small"
                        renderValue={(selected) =>
                          selected
                            ?.map((id) => ourEntities?.find((oe) => oe.id === id)?.Name)
                            .join(', ') || 'Select some options'
                        }
                      >
                        {ourEntities?.map(({ id, Name }) => (
                          <MenuItem value={id} key={id}>
                            <Checkbox size="small" checked={value.includes(id as string)} />
                            <ListItemText primary={Name} />
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6} pr={1}>
                  <WsSelect
                    required
                    label="Contract Type"
                    name="contractType"
                    selectArray={contractTypes}
                  />
                </Grid>
                <Grid item xs={12} sm={6} pr={1}>
                  <Label>Client Contact Name</Label>
                  <Controller
                    control={methods.control}
                    name="clientContact"
                    render={({ field: { onChange } }) => (
                      <Autocomplete
                        size="small"
                        selectOnFocus
                        clearOnBlur={false}
                        handleHomeEndKeys
                        options={clientContactOptions as []}
                        getOptionLabel={clientContactAutocompleteProps.getOptionLabel}
                        onChange={(event, item) => onChange(item)}
                        onInputChange={async (event, value) => {
                          const res = await getClientContactAutocompleteOptions(value, clientId);
                          const options = res.data;
                          options.forEach((option) => {
                            option.autocompleteLabel = getAutocompleteLabelForClientContact(option);
                            if (!option.id) {
                              option.id = option.value;
                            }
                          });
                          setClientContactOptions(options);
                        }}
                        renderInput={(params) => <TextField {...params} />}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6} pr={1}>
                  <Label>Contract Start Date</Label>
                  <Controller
                    control={methods.control}
                    name="startDate"
                    render={({ field: { onChange, value = null } }) => (
                      <LocalizationProvider dateAdapter={DateAdapter}>
                        <DatePicker
                          value={value ?? null}
                          onChange={onChange}
                          renderInput={(params) => (
                            <TextField size="small" fullWidth {...params} required />
                          )}
                          inputFormat="dd/MM/yyyy"
                        />
                      </LocalizationProvider>
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6} pr={1}>
                  <Label>Contract End Date</Label>
                  <Controller
                    control={methods.control}
                    name="endDate"
                    render={({ field: { onChange, value = null } }) => (
                      <LocalizationProvider dateAdapter={DateAdapter}>
                        <DatePicker
                          value={value ?? null}
                          onChange={onChange}
                          renderInput={(params) => (
                            <TextField size="small" fullWidth {...params} required />
                          )}
                          inputFormat="dd/MM/yyyy"
                        />
                      </LocalizationProvider>
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <WsSelect
                    required
                    label="Position Category"
                    name="feeArrangementLevel"
                    selectArray={feeArrLevelOptions}
                  />
                </Grid>
                <Grid item xs={12} sm={6} pr={1}>
                  <Label>Employment Guarantee Period</Label>
                  <Stack direction="row" spacing={1}>
                    <ControlledTextInput
                      fullWidth
                      type="number"
                      name="employmentGuarantee"
                      placeholder="Employment Guarantee"
                      size="small"
                      inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                      required
                      showMandatorySymbol={true}
                    />
                    <WsSelect
                      required
                      name="empGuaranteePeriodUnit"
                      selectArray={empGuaranteePeriodUnits}
                    />
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Label>Service Fees</Label>
                  <Stack direction="row" spacing={1}>
                    <Controller
                      control={methods.control}
                      name="feeType"
                      render={({ field: { onChange, value } }) => (
                        <WsToggleButton
                          buttonGroupPadding={6}
                          buttonsList={feeTypes}
                          defaultValue={value ?? 'FIXED'}
                          selectedIndexValue={onChange}
                        />
                      )}
                    />
                    <Box sx={{ flexGrow: 1 }}>
                      {feeType === 'PERCENTAGE' ? (
                        <Controller
                          control={methods.control}
                          name="fee"
                          rules={{ required: 'Please enter service fees.' }}
                          render={({ field: { onChange }, fieldState }) => (
                            <Autocomplete
                              freeSolo
                              size="small"
                              selectOnFocus
                              handleHomeEndKeys
                              options={['5', '10']}
                              onInputChange={(event, value) => onChange(value)}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Service Fees"
                                  error={!!fieldState.error}
                                  helperText={fieldState.error?.message}
                                />
                              )}
                            />
                          )}
                        />
                      ) : (
                        <Stack direction="row" spacing={1}>
                          <CurrencyDropdown name="currency.id" />
                          <ControlledTextInput
                            fullWidth
                            name="fee"
                            required
                            placeholder="Service Fees"
                            size="small"
                            inputProps={{}}
                          />
                        </Stack>
                      )}
                    </Box>
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <WsButton variant="outlined" onClick={handleClose}>
              Close
            </WsButton>
            <WsButton variant="contained" size="medium" type="submit">
              Submit
            </WsButton>
          </DialogActions>
        </form>
      </FormProvider>
    </WsDialog>
  );
};

export default AddFeeArrangementDialog;
